import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { clients, storage, type Client } from '../lib/api';
import { useClients } from '../hooks/useClients';
import { Plus, Search, Users, X, Loader2, LayoutGrid, TableProperties, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import PageHeader from '../components/PageHeader';
import ScoreBadge from '../components/ScoreBadge';

const STATUS_OPTS = [
  { value: '', label: 'Todos' },
  { value: 'cold_lead', label: 'Cold Lead' },
  { value: 'warm_lead', label: 'Warm Lead' },
  { value: 'active', label: 'Activo' },
  { value: 'churned', label: 'Perdido' },
];

export default function ClientsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [view, setView] = useState<'grid' | 'table'>('grid');
  const [sortKey, setSortKey] = useState<'name' | 'score' | 'date' | 'status'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data: clientsData, isLoading, refetch: refetchClients } = useClients({
    search: search || undefined,
    status: status || undefined,
    page,
  });

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = clientsData ? Math.ceil(clientsData.total / clientsData.limit) : 1;

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={12} className="text-gray-300" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="text-blue-600" /> : <ArrowDown size={12} className="text-blue-600" />;
  };

  const STATUS_ORDER: Record<string, number> = { active: 0, warm_lead: 1, cold_lead: 2, churned: 3 };

  const sortedClients = useMemo(() => {
    if (!clientsData) return [];
    const list = [...clientsData.clients];
    list.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name': cmp = a.business_name.localeCompare(b.business_name); break;
        case 'score': cmp = (a.latest_audit_score ?? -1) - (b.latest_audit_score ?? -1); break;
        case 'status': cmp = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9); break;
        case 'date': cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [clientsData, sortKey, sortDir]);

  return (
    <div className="space-y-6">
      <PageHeader
        type="clients"
        icon={Users}
        title="Tenants"
        subtitle={clientsData ? `${clientsData.total} tenant${clientsData.total !== 1 ? 's' : ''}` : '…'}
        actions={
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shrink-0"
          >
            <Plus size={16} />
            Nuevo Cliente
          </button>
        }
      />

      {/* Filters + View Toggle */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o dominio…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
          />
        </div>
        <select
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          {STATUS_OPTS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setView('grid')}
            className={`p-2 ${view === 'grid' ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-400 hover:text-gray-600'} transition-colors`}
            title="Vista cuadrícula"
          >
            <LayoutGrid size={16} />
          </button>
          <button
            onClick={() => setView('table')}
            className={`p-2 ${view === 'table' ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-400 hover:text-gray-600'} transition-colors`}
            title="Vista tabla"
          >
            <TableProperties size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400 animate-pulse">Cargando…</div>
      ) : !clientsData || clientsData.clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">No se encontraron clientes</p>
          <button
            onClick={() => setShowNew(true)}
            className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Crear primer cliente →
          </button>
        </div>
      ) : (
        <>
          {view === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedClients.map(c => (
                <Link
                  key={c.id}
                  to={`/clients/${c.id}`}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <ScoreBadge score={c.latest_audit_score} size="sm" />
                      {c.logo_key ? (
                        <img
                          src={storage.inlineUrl(c.logo_key)}
                          alt={c.business_name}
                          className="w-8 h-8 rounded-full object-contain bg-gray-50"
                          onError={e => {
                            const el = e.currentTarget;
                            el.style.display = 'none';
                            el.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs${c.logo_key ? ' hidden' : ''}`}>
                        {c.business_name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ClientStatusBadge status={c.status} />
                      {c.assigned_users && c.assigned_users.length > 0 && (
                        <div className="flex -space-x-1.5">
                          {c.assigned_users.slice(0, 3).map(u => (
                            <div key={u.id} title={u.name}>
                              {u.photo_key ? (
                                <img src={storage.inlineUrl(u.photo_key)} alt={u.name} className="w-6 h-6 rounded-full object-cover ring-2 ring-white" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-[10px] ring-2 ring-white">
                                  {u.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                          ))}
                          {c.assigned_users.length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-[10px] ring-2 ring-white">
                              +{c.assigned_users.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                    {c.business_name}
                  </h3>
                  {c.domain && (
                    <p className="text-sm text-gray-400 mt-0.5">{c.domain}</p>
                  )}
                  <p className="text-xs text-gray-300 mt-2">
                    {formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: es })}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-500">
                      <button onClick={() => toggleSort('score')} className="flex items-center gap-1 hover:text-gray-700">
                        Score <SortIcon col="score" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500">
                      <button onClick={() => toggleSort('name')} className="flex items-center gap-1 hover:text-gray-700">
                        Negocio <SortIcon col="name" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">Dominio</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500">
                      <button onClick={() => toggleSort('status')} className="flex items-center gap-1 hover:text-gray-700">
                        Estado <SortIcon col="status" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">Asignado</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">Contacto</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-500">
                      <button onClick={() => toggleSort('date')} className="flex items-center gap-1 ml-auto hover:text-gray-700">
                        Creado <SortIcon col="date" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sortedClients.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/clients/${c.id}`}>
                          <ScoreBadge score={c.latest_audit_score} size="sm" />
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/clients/${c.id}`} className="flex items-center gap-2">
                          {c.logo_key ? (
                            <img src={storage.inlineUrl(c.logo_key)} alt="" className="w-6 h-6 rounded-full object-contain bg-gray-50 shrink-0" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-[10px] shrink-0">
                              {c.business_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="font-medium text-gray-900 hover:text-blue-700">
                            {c.business_name}
                          </span>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-400 hidden md:table-cell">
                        {c.domain && (
                          <a href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">{c.domain}</a>
                        )}
                      </td>
                      <td className="px-4 py-3"><ClientStatusBadge status={c.status} /></td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {c.assigned_users && c.assigned_users.length > 0 ? (
                          <div className="flex items-center -space-x-1">
                            {c.assigned_users.slice(0, 3).map(u => (
                              <div key={u.id} title={u.name}>
                                {u.photo_key ? (
                                  <img src={storage.inlineUrl(u.photo_key)} alt={u.name} className="w-5 h-5 rounded-full object-cover ring-1 ring-white" />
                                ) : (
                                  <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-[9px] ring-1 ring-white">
                                    {u.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                            ))}
                            {c.assigned_users.length <= 2 && (
                              <span className="text-xs text-gray-500 ml-2">{c.assigned_users.map(u => u.name.split(' ')[0]).join(', ')}</span>
                            )}
                            {c.assigned_users.length > 3 && (
                              <span className="text-xs text-gray-400 ml-1">+{c.assigned_users.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell truncate max-w-[180px]">
                        {c.contact_name || c.contact_email || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-400 text-xs whitespace-nowrap">
                        {formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: es })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Página {page} de {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* New Client Modal */}
      {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); refetchClients(); }} />}
    </div>
  );
}

function ClientStatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    cold_lead: 'bg-slate-100 text-slate-600',
    warm_lead: 'bg-amber-100 text-amber-700',
    active: 'bg-green-100 text-green-700',
    churned: 'bg-gray-100 text-gray-500',
    lead: 'bg-blue-100 text-blue-700',
  };
  const labels: Record<string, string> = {
    cold_lead: 'Cold Lead',
    warm_lead: 'Warm Lead',
    active: 'Activo',
    churned: 'Perdido',
    lead: 'Lead',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${variants[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {labels[status] ?? status}
    </span>
  );
}

function NewClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    business_name: '',
    domain: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: '',
    status: 'cold_lead' as Client['status'],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.domain.trim()) {
      setError('El dominio es obligatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await clients.create({
        business_name: form.business_name.trim() || undefined,
        domain: form.domain.trim(),
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        notes: form.notes.trim() || null,
        status: form.status,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear cliente');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Nuevo Cliente</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dominio *</label>
            <input
              type="text"
              value={form.domain}
              onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}
              placeholder="ejemplo.es"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del negocio <span className="font-normal text-gray-400">(autodetectado con auditoría)</span></label>
            <input
              type="text"
              value={form.business_name}
              onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
              placeholder="Se detecta automáticamente al auditar"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contacto</label>
              <input
                type="text"
                value={form.contact_name}
                onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input
                type="text"
                value={form.contact_phone}
                onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.contact_email}
              onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value as Client['status'] }))}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="cold_lead">Cold Lead</option>
              <option value="warm_lead">Warm Lead</option>
              <option value="active">Activo</option>
              <option value="churned">Perdido</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Crear Cliente
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
